import React, { useRef, useState } from "react";
import { FiFile, FiPlus, FiUploadCloud, FiX } from "react-icons/fi";
import styles from "./Support.module.scss";

export const formatFileSize = (sizeBytes: number) => {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }

  const units = ["KB", "MB", "GB"];
  let value = sizeBytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(1)} ${units[unitIndex]}`;
};

interface AttachmentDropzoneProps {
  files: File[];
  maxFiles: number;
  accept: string;
  hint: string;
  error?: string;
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
}

// One drop target for both states: empty (click or drop anywhere) and with files
// (compact rows, "Add more files", still droppable).
const AttachmentDropzone = ({
  files,
  maxFiles,
  accept,
  hint,
  error,
  onAddFiles,
  onRemoveFile,
}: AttachmentDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const openFileDialog = () => inputRef.current?.click();

  const dropHandlers = {
    onDragOver: (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: () => setIsDragging(false),
    onDrop: (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      onAddFiles(Array.from(event.dataTransfer.files || []));
    },
  };
  const activeClass = isDragging ? ` ${styles.dropzoneActive}` : "";

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        hidden
        onChange={(event) => {
          onAddFiles(Array.from(event.target.files || []));
          event.target.value = "";
        }}
      />

      {files.length === 0 ? (
        <div
          role="button"
          tabIndex={0}
          aria-label="Add attachments: drop files here or browse"
          onClick={openFileDialog}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openFileDialog();
            }
          }}
          className={`${styles.dropzone}${activeClass}`}
          {...dropHandlers}
        >
          <FiUploadCloud className={styles.dropzoneIcon} aria-hidden />
          <span className={styles.dropzoneTitle}>Drop files here</span>
          <span>
            or <span className={styles.dropzoneLink}>Browse files</span>
          </span>
          <span className={styles.dropzoneHint}>{hint}</span>
        </div>
      ) : (
        <div className={`${styles.fileList}${activeClass}`} {...dropHandlers}>
          {files.map((file, index) => (
            <div
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className={styles.fileRow}
            >
              <FiFile aria-hidden />
              <span className={styles.fileName} title={file.name}>
                {file.name}
              </span>
              <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
              <button
                type="button"
                onClick={() => onRemoveFile(index)}
                aria-label={`Remove ${file.name}`}
                className={styles.fileRemove}
              >
                <FiX />
              </button>
            </div>
          ))}
          <div className={styles.fileListFooter}>
            {files.length < maxFiles ? (
              <button type="button" onClick={openFileDialog} className={styles.addMore}>
                <FiPlus aria-hidden /> Add more files
              </button>
            ) : (
              <span />
            )}
            <span className={styles.dropzoneHint}>
              {files.length} of {maxFiles} files
            </span>
          </div>
        </div>
      )}

      {error ? (
        <span className="tw-text-xs tw-font-semibold tw-text-rose-600">{error}</span>
      ) : null}
    </div>
  );
};

export default AttachmentDropzone;
